import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { usePlatformSettings } from '../contexts/PlatformSettingsContext';

// Create context for dashboard refresh
export const UltraAdminContext = createContext();
export const useUltraAdminContext = () => useContext(UltraAdminContext);
import { 
  CrownIcon, 
  LayoutDashboardIcon,
  BuildingIcon, 
  UsersIcon,
  CreditCardIcon, 
  BarChart3Icon,
  SettingsIcon,
  FileTextIcon,
  ToggleLeftIcon,
  LogOutIcon,
  MenuIcon,
  XIcon,
  BookOpenIcon,
  UserCircleIcon,
  DatabaseIcon,
  CoinsIcon,
  ShieldAlertIcon,
  SearchIcon,
  HardDriveIcon,
  MapIcon,
  MessageCircleIcon,
  MessageSquareQuoteIcon,
  GiftIcon
} from 'lucide-react';

const UltraAdminLayout = () => {
  const { settings } = usePlatformSettings();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    // Check Ultra Admin authentication
    const ultraToken = localStorage.getItem('ultra_admin_token');
    if (!ultraToken) {
      navigate('/ultra-admin');
      return;
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('ultra_admin_token');
    localStorage.removeItem('ultra_admin_user');
    navigate('/');
  };

  const menuItems = [
    { 
      id: 'dashboard', 
      name: 'Dashboard', 
      path: '/ultra-admin/dashboard', 
      icon: LayoutDashboardIcon,
      description: 'Genel Bakış'
    },
    { 
      id: 'tenants', 
      name: 'Kiracı Yönetimi', 
      path: '/ultra-admin/tenants', 
      icon: BuildingIcon,
      description: 'Şirket & Hesap Yönetimi'
    },
    { 
      id: 'users', 
      name: 'Kullanıcı Yönetimi', 
      path: '/ultra-admin/users', 
      icon: UsersIcon,
      description: 'Platform Kullanıcıları'
    },
    { 
      id: 'billing', 
      name: 'Faturalama', 
      path: '/ultra-admin/billing', 
      icon: CreditCardIcon,
      description: 'Ödemeler & Abonelikler'
    },
    {
      id: 'promo-codes',
      name: 'Promosyonlar',
      path: '/ultra-admin/promo-codes',
      icon: GiftIcon,
      description: 'Ücretsiz / indirimli davet kodları'
    },
    { 
      id: 'analytics', 
      name: 'Analitik & Raporlar', 
      path: '/ultra-admin/analytics', 
      icon: BarChart3Icon,
      description: 'Performans Metrikleri'
    },
    { 
      id: 'features', 
      name: 'Özellik Yönetimi', 
      path: '/ultra-admin/features', 
      icon: ToggleLeftIcon,
      description: 'Feature Flags & Versiyonlama'
    },
    {
      id: 'plan-comparison',
      name: 'Karşılaştırma Matrisi',
      path: '/ultra-admin/plan-comparison',
      icon: ToggleLeftIcon,
      description: '/pricing tablosunu tek ekrandan yönet'
    },
    {
      id: 'whatsapp-quotas',
      name: 'WhatsApp Kotaları',
      path: '/ultra-admin/whatsapp-quotas',
      icon: MessageCircleIcon,
      description: 'Plan başına günlük mesaj limitleri'
    },
    {
      id: 'testimonials',
      name: 'Referanslar',
      path: '/ultra-admin/testimonials',
      icon: MessageSquareQuoteIcon,
      description: 'Landing sayfası müşteri yorumları'
    },
    { 
      id: 'storage-usage', 
      name: 'Depolama Kullanımı', 
      path: '/ultra-admin/storage-usage', 
      icon: HardDriveIcon,
      description: 'Tenant başına quota & upgrade sinyali'
    },
    { 
      id: 'roadmap', 
      name: 'Yol Haritası', 
      path: '/ultra-admin/roadmap', 
      icon: MapIcon,
      description: 'P0/P1/P2 sürüm planı & CHANGELOG'
    },
    { 
      id: 'audit', 
      name: 'Denetim Kayıtları', 
      path: '/ultra-admin/audit', 
      icon: FileTextIcon,
      description: 'Sistem Logları'
    },
    { 
      id: 'impersonation-logs', 
      name: 'Impersonation Logları', 
      path: '/ultra-admin/impersonation-logs', 
      icon: ShieldAlertIcon,
      description: 'Kullanıcı Olarak Giriş Geçmişi'
    },
    { 
      id: 'settings', 
      name: 'Platform Ayarları', 
      path: '/ultra-admin/settings', 
      icon: SettingsIcon,
      description: 'Sistem Konfigürasyonu'
    },
    { 
      id: 'collections', 
      name: 'Collections', 
      path: '/ultra-admin/collections', 
      icon: DatabaseIcon,
      description: 'MongoDB Veritabanı Yönetimi'
    },
    { 
      id: 'rules', 
      name: 'Platform Kuralları', 
      path: '/ultra-admin/rules', 
      icon: BookOpenIcon,
      description: 'Kütüphane & İş Kuralları'
    },
    { 
      id: 'library', 
      name: 'Kütüphane', 
      path: '/ultra-admin/library', 
      icon: BookOpenIcon,
      description: 'Ülke, Şehir, Para Birimi & Telefon Kodları'
    },
    { 
      id: 'terms', 
      name: 'Kullanıcı Sözleşmesi', 
      path: '/ultra-admin/terms', 
      icon: FileTextIcon,
      description: 'Hukuki Sözleşme & Şartlar'
    }
  ];

  const activeMenuItem = menuItems.find(item => location.pathname === item.path);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div 
        className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 shadow-2xl flex flex-col`}
        style={{
          background: `linear-gradient(to bottom, ${settings.primary_color || '#7C3AED'}, ${settings.secondary_color || '#4F46E5'})`
        }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-purple-800/50">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${sidebarOpen ? 'space-x-3' : 'justify-center'}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <CrownIcon className="w-6 h-6 text-white" />
              </div>
              {sidebarOpen && (
                <div>
                  <h1 className="text-white font-bold text-lg">Ultra Admin</h1>
                  <p className="text-purple-200 text-xs">Platform Control</p>
                </div>
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-purple-200 hover:text-white transition-colors p-1"
            >
              {sidebarOpen ? <XIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {sidebarOpen && (
          <div className="px-4 py-3 border-b border-purple-800/50">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-300" />
              <input
                type="text"
                placeholder="Menüde ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-purple-400/30 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
              />
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.filter(item => 
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(searchQuery.toLowerCase())
          ).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.id}
                to={item.path}
                className={`flex items-center space-x-3 px-3 py-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/30' 
                    : 'text-purple-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-yellow-300' : 'text-purple-300 group-hover:text-white'}`} />
                {sidebarOpen && (
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.name}</div>
                    <div className="text-xs opacity-75 truncate">{item.description}</div>
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-purple-800/50">
          <button
            onClick={handleLogout}
            className={`flex items-center space-x-3 px-3 py-3 rounded-xl text-purple-200 hover:bg-red-500/20 hover:text-white transition-all duration-200 w-full ${
              sidebarOpen ? '' : 'justify-center'
            }`}
          >
            <LogOutIcon className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">Çıkış Yap</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {activeMenuItem?.name || 'Ultra Admin Panel'}
              </h2>
              <p className="text-gray-600 text-sm">
                {activeMenuItem?.description || 'Advancecash.io Platform Yönetimi'}
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">Ultra Admin</p>
                <p className="text-xs text-gray-500">Platform Yöneticisi</p>
              </div>
              <Link
                to="/ultra-admin/profile"
                className="relative group"
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:ring-4 hover:ring-purple-200 cursor-pointer"
                  style={{
                    background: `linear-gradient(to bottom right, ${settings.primary_color || '#7C3AED'}, ${settings.secondary_color || '#4F46E5'})`
                  }}
                >
                  <UserCircleIcon className="w-5 h-5 text-white" />
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
              </Link>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 p-6 bg-gray-50 overflow-auto">
          <UltraAdminContext.Provider value={{ triggerRefresh, refreshTrigger }}>
            <Outlet />
          </UltraAdminContext.Provider>
        </div>
      </div>
    </div>
  );
};

export default UltraAdminLayout;